import { StartIcon, StopIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { useConfirmation } from '@components/modal';
import { useStartEditorMutation, useStopEditorMutation, type Editor } from '@generated/graphql';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';

type ServerPowerButtonProps = {
  server: Pick<Editor, 'editorId' | 'workspaceId' | 'status'>;
  onChange?: () => void;
};

export const ServerPowerButton = ({ server, onChange }: ServerPowerButtonProps) => {
  const { editorId, workspaceId, status } = server;

  const { showConfirmation } = useConfirmation();
  const { createAsyncNotification } = useNotifications();

  const [startServer] = useStartEditorMutation();
  const [stopServer] = useStopEditorMutation();

  const onServerStart = createAsyncNotification(
    'Server starting...',
    async () => {
      posthog.capture('agent-studio:user_start-server_submitted', { workspaceId, editorId });
      await startServer({
        variables: { editorId },
        onCompleted: () => {
          posthog.capture('agent-studio:user_start-server_completed', { workspaceId, editorId });
          onChange?.();
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_start-server_failed', {
            workspaceId,
            editorId,
            error: (error as Error)?.message || 'An error occurred during start',
          });
          throw error;
        },
      });
    },
    {
      loadingMessage: 'Requesting server start...',
    },
  );
  const onServerStop = () => {
    showConfirmation({
      onAffirm: createAsyncNotification(
        'Server stopping...',
        async () => {
          posthog.capture('agent-studio:user_stop-server_submitted', { workspaceId, editorId });
          await stopServer({
            variables: { editorId },
            onCompleted: () => {
              posthog.capture('agent-studio:user_stop-server_completed', { workspaceId, editorId });
              onChange?.();
            },
            onError: (error) => {
              posthog.capture('agent-studio:user_stop-server_failed', {
                workspaceId,
                editorId,
                error: (error as Error)?.message || 'An error occurred during stop',
              });
              throw error;
            },
          });
        },
        {
          loadingMessage: 'Requesting server stop...',
        },
      ),
      message: `Are you sure you want to stop the server: ${editorId}?`,
    });
  };

  const serverIsRunning = status?.state.toLowerCase() === 'running';
  const serverIsStopped = status?.state.toLowerCase() === 'stopped';

  if (!serverIsRunning && !serverIsStopped) {
    return null;
  }
  return (
    <IconButton
      Icon={serverIsRunning ? StopIcon : StartIcon}
      size={16}
      variant={serverIsRunning ? 'error' : 'success'}
      tooltip={serverIsRunning ? 'Stop server' : 'Start server'}
      onClick={serverIsRunning ? onServerStop : onServerStart}
    />
  );
};
